import { useState, useRef } from "react";
import api, { brl, formatErr } from "../lib/api";
import { toast } from "sonner";
import {
  FileUp, Upload, Trash2, CheckCircle, Loader, FileText,
  Image as ImageIcon, Info, Plus, Package,
} from "lucide-react";

export default function ImportNF() {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("pdf"); // pdf | image
  const [extracting, setExtracting] = useState(false);
  const [products, setProducts] = useState([]);
  const [markup, setMarkup] = useState(2.5);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const onFile = (f) => {
    if (!f) return;
    if (f.type === "application/pdf") setMode("pdf");
    else if (f.type.startsWith("image/")) setMode("image");
    else { toast.error("Envie um PDF ou imagem"); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("Arquivo muito grande (máx 15MB)"); return; }
    setFile(f); setProducts([]); setResult(null);
  };

  const extract = async () => {
    if (!file) { toast.error("Envie uma NF primeiro"); return; }
    setExtracting(true); setProducts([]); setResult(null);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((ok, er) => {
        reader.onload = () => ok(reader.result);
        reader.onerror = er;
        reader.readAsDataURL(file);
      });
      const payload = mode === "pdf" ? { pdf_base64: dataUrl, filename: file.name }
                                     : { image_base64: dataUrl, filename: file.name };
      const { data } = await api.post("/import-nf/extract", payload, { timeout: 120000 });
      setProducts(data.products || []);
      toast.success(`${data.count} produtos identificados!`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message || "Erro ao extrair");
    } finally { setExtracting(false); }
  };

  const updateField = (i, field, value) => {
    setProducts((arr) => arr.map((p, idx) => idx === i ? { ...p, [field]: field === "quantity" ? parseInt(value) || 0 : field === "unit_cost" ? parseFloat(value) || 0 : value } : p));
  };

  const removeItem = (i) => setProducts((arr) => arr.filter((_, idx) => idx !== i));

  const addBlank = () => setProducts((arr) => [...arr, { name: "", sku: "", quantity: 1, unit_cost: 0 }]);

  const apply = async () => {
    if (products.length === 0) { toast.error("Nenhum produto para importar"); return; }
    const invalid = products.find((p) => !p.name.trim());
    if (invalid) { toast.error("Todos os produtos precisam ter um nome"); return; }
    setApplying(true);
    try {
      const { data } = await api.post("/import-nf/apply", {
        products, markup: Number(markup) || 2.5, source_name: file?.name,
      });
      setResult(data);
      toast.success(`✅ ${data.inserted} novos, ${data.updated} atualizados`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Erro ao importar");
    } finally { setApplying(false); }
  };

  const reset = () => {
    setFile(null); setProducts([]); setResult(null); setMarkup(2.5);
    if (inputRef.current) inputRef.current.value = "";
  };

  const total = products.reduce((s, p) => s + (p.quantity || 0) * (p.unit_cost || 0), 0);

  return (
    <div className="space-y-6" data-testid="import-nf-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">Estoque</div>
        <h1 className="font-display text-4xl font-bold">Importar Nota Fiscal</h1>
        <p className="text-slate-500 text-sm mt-1">
          Envie o PDF da NF (DANFE) ou uma foto — a IA extrai os produtos e cadastra/atualiza o estoque automaticamente.
        </p>
      </header>

      {/* Step 1: upload */}
      {products.length === 0 && !result && (
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          {!file ? (
            <button
              onClick={() => inputRef.current?.click()}
              data-testid="nf-upload-btn"
              className="w-full aspect-[4/1] border-2 border-dashed border-slate-300 rounded-sm flex flex-col items-center justify-center hover:border-[#2563EB] hover:bg-blue-50 text-slate-500 hover:text-[#2563EB]"
            >
              <Upload size={36} />
              <span className="mt-2 font-semibold">Enviar nota fiscal</span>
              <span className="text-xs opacity-70">PDF (DANFE) ou foto JPG/PNG — até 15MB</span>
            </button>
          ) : (
            <div className="flex items-center justify-between gap-4 border border-slate-200 p-4 rounded-sm bg-slate-50">
              <div className="flex items-center gap-3">
                {file.type === "application/pdf" ? <FileText size={32} className="text-red-500" /> : <ImageIcon size={32} className="text-[#2563EB]" />}
                <div>
                  <div className="font-semibold text-sm">{file.name}</div>
                  <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(0)}KB • {mode.toUpperCase()}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => inputRef.current?.click()} data-testid="nf-replace-btn" className="text-xs px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-white">
                  Trocar
                </button>
                <button onClick={reset} data-testid="nf-reset-btn" className="text-xs px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-red-50 text-red-500">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          )}
          <input ref={inputRef} type="file" accept="application/pdf,image/*" className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])} data-testid="nf-file-input" />

          {file && (
            <button
              onClick={extract} disabled={extracting}
              data-testid="nf-extract-btn"
              className="w-full mt-4 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 text-white font-semibold py-3 rounded-sm flex items-center justify-center gap-2"
            >
              {extracting ? <><Loader className="animate-spin" size={16} /> Analisando NF...</> : <><FileUp size={16} /> Extrair produtos da nota</>}
            </button>
          )}

          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-blue-900">
            <Info size={12} className="inline mr-1" /> Dica: PDF da DANFE dá o melhor resultado. A IA também reconhece fotos nítidas da nota.
          </div>
        </div>
      )}

      {/* Step 2: review products */}
      {products.length > 0 && !result && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
              <div>
                <div className="font-display text-lg font-bold">Revisar produtos extraídos</div>
                <div className="text-sm text-slate-500">{products.length} itens • Total: {brl(total)}</div>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-600">Markup padrão:</label>
                <input type="number" step="0.1" value={markup} onChange={(e) => setMarkup(e.target.value)}
                  data-testid="nf-markup"
                  className="w-20 px-2 py-1 border border-slate-300 rounded-sm text-sm" />
                <span className="text-xs text-slate-400">x (venda = custo × markup)</span>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              ✏️ Você pode editar qualquer campo antes de confirmar. Produtos com SKU existente terão <strong>estoque somado</strong>; novos serão <strong>criados</strong>.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="text-left p-2">Nome do produto</th>
                    <th className="text-left p-2">SKU</th>
                    <th className="text-right p-2 w-20">Qtd</th>
                    <th className="text-right p-2 w-28">Custo unit.</th>
                    <th className="text-right p-2 w-24">Subtotal</th>
                    <th className="p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`nf-row-${i}`}>
                      <td className="p-2">
                        <input type="text" value={p.name} onChange={(e) => updateField(i, "name", e.target.value)}
                          data-testid={`nf-name-${i}`}
                          className="w-full px-2 py-1 border border-slate-200 rounded-sm text-sm" />
                      </td>
                      <td className="p-2">
                        <input type="text" value={p.sku} onChange={(e) => updateField(i, "sku", e.target.value)}
                          data-testid={`nf-sku-${i}`}
                          className="w-28 px-2 py-1 border border-slate-200 rounded-sm text-sm" />
                      </td>
                      <td className="p-2">
                        <input type="number" value={p.quantity} onChange={(e) => updateField(i, "quantity", e.target.value)}
                          data-testid={`nf-qty-${i}`}
                          className="w-16 px-2 py-1 border border-slate-200 rounded-sm text-sm text-right" />
                      </td>
                      <td className="p-2">
                        <input type="number" step="0.01" value={p.unit_cost} onChange={(e) => updateField(i, "unit_cost", e.target.value)}
                          data-testid={`nf-cost-${i}`}
                          className="w-24 px-2 py-1 border border-slate-200 rounded-sm text-sm text-right" />
                      </td>
                      <td className="p-2 text-right text-slate-600 text-sm">{brl((p.quantity || 0) * (p.unit_cost || 0))}</td>
                      <td className="p-2 text-right">
                        <button onClick={() => removeItem(i)} data-testid={`nf-remove-${i}`} className="text-red-500 hover:bg-red-50 p-1 rounded-sm">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button onClick={addBlank} data-testid="nf-add-blank" className="mt-3 text-xs text-[#2563EB] hover:underline flex items-center gap-1">
              <Plus size={12} /> Adicionar item em branco
            </button>
          </div>

          <div className="flex gap-2 justify-end">
            <button onClick={reset} data-testid="nf-cancel-btn" className="px-4 py-2 text-sm border border-slate-300 rounded-sm">
              Cancelar
            </button>
            <button onClick={apply} disabled={applying}
              data-testid="nf-apply-btn"
              className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold px-6 py-2 rounded-sm flex items-center gap-2">
              {applying ? <><Loader className="animate-spin" size={14} /> Importando...</> : <><CheckCircle size={14} /> Confirmar e Importar {products.length} produtos</>}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: result */}
      {result && (
        <div className="bg-white border border-slate-200 rounded-sm p-8 text-center" data-testid="nf-result">
          <CheckCircle size={64} className="mx-auto text-emerald-500 mb-4" />
          <h2 className="font-display text-2xl font-bold">Importação concluída!</h2>
          <div className="grid grid-cols-3 gap-4 mt-6 max-w-xl mx-auto">
            <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-sm">
              <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Novos</div>
              <div className="font-display text-3xl font-bold text-emerald-600">{result.inserted}</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-sm">
              <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold">Atualizados</div>
              <div className="font-display text-3xl font-bold text-blue-600">{result.updated}</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 p-4 rounded-sm">
              <div className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Total</div>
              <div className="font-display text-3xl font-bold text-slate-700">{result.total}</div>
            </div>
          </div>
          <div className="flex gap-2 justify-center mt-6">
            <button onClick={reset} data-testid="nf-new-import-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold px-6 py-2.5 rounded-sm flex items-center gap-2">
              <FileUp size={14} /> Importar outra NF
            </button>
            <a href="/app/products" data-testid="nf-view-products-btn" className="border border-slate-300 hover:bg-slate-50 font-semibold px-6 py-2.5 rounded-sm flex items-center gap-2 text-slate-700">
              <Package size={14} /> Ver estoque
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
